import React, { useEffect, useMemo, useRef, useState } from 'react'
import useSWR from 'swr'
import { CSVLink } from 'react-csv'
import {
  ActionIcon,
  Alert,
  alpha,
  Anchor,
  Box,
  Button,
  FileButton,
  Group,
  ScrollArea,
  Stack,
  Text,
  ThemeIcon,
  Title,
  Tooltip,
} from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { notifications } from '@mantine/notifications'
import { DataTable } from 'mantine-datatable'
import {
  DownloadSimpleIcon,
  FadersIcon,
  PencilSimpleIcon,
  PlusIcon,
  QrCodeIcon,
  StorefrontIcon,
  TrashIcon,
} from '@phosphor-icons/react'
import Client from '../../models/client'
import { bulkDeleteShops, createByCsv, deleteShop, downloadShopsQrZip, fetchShops } from '../api'
import { BaseModal } from '../components/BaseModal'
import { PREFECTURE_LIST } from '../components/Global'
import { PrefectureOrderModal } from '../components/PrefectureOrderModal'
import { ShopFormModal } from '../components/ShopFormModal'
import { ShopsOptionModal } from '../components/ShopsOptionModal'
import { TablePagination } from '../components/TablePagination'
import { downloadBlob } from '../utils/downloadBlob'
import dayjs from 'dayjs'
import { confirmBulkDelete } from '../utils/confirm'

interface Props {
  client: Client
}

type ShopRow = {
  id: number
  code: string
  zip_code: string
  prefecture_id: number
  address1: string
  address2: string
  address3: string
  name: string
  custom_name: string
  kana: string
  kana_eng: string
  category1: string
  category2: string
  category3: string
  invite_hidden: boolean
  guest_hidden: boolean
  coming_hidden: boolean
  notification_email: string
  shop_page_url: string
}

const UploadLeftSection = <PlusIcon size={16} weight='bold' />
const downloadLeftSection = <DownloadSimpleIcon size={16} weight='bold' />
const optionLeftSection = <FadersIcon size={16} weight='fill' />

const prefectureName = (prefectureId: number): string => {
  const item = PREFECTURE_LIST.find((p) => p.id === prefectureId)
  return item ? item.name : ''
}

const hiddenLabel = (value: boolean): string => (value ? '非表示' : '表示')

// 一括操作バー
const bulkBannerStyles = {
  root: { background: alpha('var(--mantine-color-invy-6)', 0.06), border: '1px solid var(--mantine-color-invy-6)' },
}

const csvColumns = [
  { label: '店舗コード', key: 'code' },
  { label: '郵便番号', key: 'zip_code' },
  { label: '都道府県', key: 'prefecture_name' },
  { label: '住所1', key: 'address1' },
  { label: '住所2', key: 'address2' },
  { label: '住所3', key: 'address3' },
  { label: '店舗名', key: 'name' },
  { label: '店舗名(表示名)', key: 'custom_name' },
  { label: '店舗名カナ', key: 'kana' },
  { label: '店舗名英語', key: 'kana_eng' },
  { label: 'カテゴリー1', key: 'category1' },
  { label: 'カテゴリー2', key: 'category2' },
  { label: 'カテゴリー3', key: 'category3' },
  { label: '招待非表示', key: 'invite_hidden' },
  { label: 'ゲスト非表示', key: 'guest_hidden' },
  { label: '来店非表示', key: 'coming_hidden' },
  { label: '通知用メールアドレス', key: 'notification_email' },
  { label: '店舗ページURL', key: 'shop_page_url' },
]

const QR_ZIP_MAX_SHOPS = 500

const ShopsContainer: React.FC<Props> = ({ client }) => {
  const [isUploading, setIsUploading] = useState(false)
  const resetFileRef = useRef<() => void>(null)
  const [isOpenedShopFormModal, shopFormModal] = useDisclosure(false)
  const [isOpenedChoiceModal, choiceModal] = useDisclosure(false)
  const [isOpenedSortModal, sortModal] = useDisclosure(false)
  const [isOpenedDeleteModal, deleteModal] = useDisclosure(false)
  const [editingShopId, setEditingShopId] = useState<number | null>(null)
  const [selectedShop, setSelectedShop] = useState<ShopRow | null>(null)
  const [deleteError, setDeleteError] = useState<string | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const [selectedShops, setSelectedShops] = useState<ShopRow[]>([])
  const [bulkDeleting, setBulkDeleting] = useState(false)
  const [qrDownloading, setQrDownloading] = useState(false)
  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState(10)

  const { data: shops, isLoading, mutate: mutateShops } = useSWR<ShopRow[]>(
    `/admin/clients/${client.id}/shops`,
    () => fetchShops(client.id),
  )

  const totalCount = shops?.length ?? 0
  const totalPages = Math.max(1, Math.ceil(totalCount / perPage))

  useEffect(() => {
    if (page > totalPages) setPage(totalPages)
  }, [page, totalPages])

  const pagedShops = useMemo(() => {
    if (!shops) return []
    const from = (page - 1) * perPage
    return shops.slice(from, from + perPage)
  }, [shops, page, perPage])

  const handleUploadCsv = async (file: File | null) => {
    if (!file) return
    const text = await file.text()
    const nonEmptyLines = text.split(/\r\n|\r|\n/).filter((line) => line.trim() !== '')
    if (nonEmptyLines.length <= 1) {
      notifications.show({
        message: '取り込めるデータがありません。CSVの内容を確認してください',
        color: 'red',
      })
      resetFileRef.current?.()
      return
    }
    setIsUploading(true)
    try {
      await createByCsv(client.id, file)
      await mutateShops()
      notifications.show({ message: 'CSVを取り込みました', color: 'teal' })
    } catch (e) {
      console.error('CSVアップロードに失敗しました', e)
      notifications.show({ message: 'CSVアップロードに失敗しました', color: 'red' })
    } finally {
      setIsUploading(false)
      resetFileRef.current?.()
    }
  }

  const csvData = useMemo(
    () =>
      (shops ?? []).map((s) => ({
        code: s.code,
        zip_code: s.zip_code,
        prefecture_name: prefectureName(s.prefecture_id),
        address1: s.address1,
        address2: s.address2,
        address3: s.address3,
        name: s.name,
        custom_name: s.custom_name,
        kana: s.kana,
        kana_eng: s.kana_eng,
        category1: s.category1,
        category2: s.category2,
        category3: s.category3,
        invite_hidden: hiddenLabel(s.invite_hidden),
        guest_hidden: hiddenLabel(s.guest_hidden),
        coming_hidden: hiddenLabel(s.coming_hidden),
        notification_email: s.notification_email,
        shop_page_url: s.shop_page_url,
      })),
    [shops],
  )

  const openDeleteModal = (shop: ShopRow) => {
    setSelectedShop(shop)
    setDeleteError(null)
    deleteModal.open()
  }

  const openCreateShopModal = () => {
    setEditingShopId(null)
    shopFormModal.open()
  }

  const openEditShopModal = (shop: ShopRow) => {
    setEditingShopId(shop.id)
    shopFormModal.open()
  }

  const handleDeleteShop = async () => {
    if (!selectedShop) return
    setDeleteError(null)
    setIsDeleting(true)
    try {
      await deleteShop(client.id, selectedShop.id)
      await mutateShops()

      // 削除済みの店舗が一括操作の選択に残らないよう除外する
      setSelectedShops((prev) => prev.filter((s) => s.id !== selectedShop.id))
      deleteModal.close()
      setSelectedShop(null)
    } catch (e) {
      console.error('店舗削除に失敗しました', e)
      setDeleteError('店舗削除に失敗しました')
    } finally {
      setIsDeleting(false)
    }
  }

  const handleBulkDelete = async () => {
    if (!confirmBulkDelete(selectedShops.length, '店舗')) return
    setBulkDeleting(true)

    try {
      await bulkDeleteShops(client.id, selectedShops.map((s) => s.id))
      await mutateShops()
      setSelectedShops([])
      notifications.show({ message: '選択した店舗を削除しました', color: 'teal' })
    } catch (e) {
      console.error('店舗の一括削除に失敗しました', e)
      notifications.show({ message: '一括削除に失敗しました', color: 'red' })
    } finally {
      setBulkDeleting(false)
    }
  }

  const handleBulkQrDownload = async () => {
    if (selectedShops.length > QR_ZIP_MAX_SHOPS) {
      notifications.show({
        message: `QRコードを一度にダウンロードできるのは${QR_ZIP_MAX_SHOPS}件までです`,
        color: 'red',
      })
      return
    }
    setQrDownloading(true)

    try {
      const blob = await downloadShopsQrZip(client.id, selectedShops.map((s) => s.id))
      downloadBlob(blob, `shop_qr_${dayjs().format('YYYYMMDD')}.zip`)
    } catch (e) {
      console.error('QRコードの一括ダウンロードに失敗しました', e)
      notifications.show({ message: 'QRコードの一括ダウンロードに失敗しました', color: 'red' })
    } finally {
      setQrDownloading(false)
    }
  }

  const tableColumns = useMemo(
    () => [
      { accessor: 'code', title: '店舗コード' },
      { accessor: 'zip_code', title: '郵便番号' },
      {
        accessor: 'prefecture_id',
        title: '都道府県',
        render: (r: ShopRow) => prefectureName(r.prefecture_id),
      },
      { accessor: 'address1', title: '住所1' },
      { accessor: 'address2', title: '住所2' },
      { accessor: 'address3', title: '住所3' },
      { accessor: 'name', title: '店舗名' },
      { accessor: 'custom_name', title: '店舗名（表示名）' },
      { accessor: 'kana', title: '店舗名カナ' },
      { accessor: 'kana_eng', title: '店舗名英語' },
      { accessor: 'category1', title: 'カテゴリー1' },
      { accessor: 'category2', title: 'カテゴリー2' },
      { accessor: 'category3', title: 'カテゴリー3' },
      {
        accessor: 'invite_hidden',
        title: '招待非表示',
        render: (r: ShopRow) => hiddenLabel(r.invite_hidden),
      },
      {
        accessor: 'guest_hidden',
        title: 'ゲスト非表示',
        render: (r: ShopRow) => hiddenLabel(r.guest_hidden),
      },
      {
        accessor: 'coming_hidden',
        title: '来店非表示',
        render: (r: ShopRow) => hiddenLabel(r.coming_hidden),
      },
      { accessor: 'notification_email', title: '通知用メールアドレス' },
      {
        accessor: 'shop_page_url',
        title: '店舗ページURL',
        render: (r: ShopRow) =>
          r.shop_page_url ? (
            <Anchor href={r.shop_page_url} target='_blank' rel='noopener noreferrer'>
              {r.shop_page_url}
            </Anchor>
          ) : null,
      },
      {
        accessor: 'qr',
        title: 'QR',
        width: 60,
        textAlign: 'center' as const,
        render: (r: ShopRow) =>
          !r.coming_hidden && (
            <Tooltip label='QRコードを保存'>
              <ActionIcon
                component='a'
                href={`/admin/api/clients/${client.id}/shops/${r.id}/qr_shop`}
                variant='subtle'
                color='gray'
              >
                <QrCodeIcon size={18} />
              </ActionIcon>
            </Tooltip>
          ),
      },
      {
        accessor: 'edit',
        title: '編集',
        width: 60,
        textAlign: 'center' as const,
        render: (r: ShopRow) => (
          <Tooltip label='編集'>
            <ActionIcon
              onClick={() => openEditShopModal(r)}
              variant='subtle'
              color='gray'
            >
              <PencilSimpleIcon size={18} />
            </ActionIcon>
          </Tooltip>
        ),
      },
      {
        accessor: 'delete',
        title: '削除',
        width: 60,
        textAlign: 'center' as const,
        render: (r: ShopRow) => (
          <Tooltip label='削除'>
            <ActionIcon
              onClick={() => openDeleteModal(r)}
              variant='subtle'
              color='red'
            >
              <TrashIcon size={18} />
            </ActionIcon>
          </Tooltip>
        ),
      },
    ],
    [client.id],
  )

  // 行数が少ないときにテーブル下部の余白が出ないよう、内部スクロールが必要な件数のときだけ
  // 高さを固定する。0件時は「データがありません」の表示領域を確保する。
  // （maxHeight は内部の ScrollArea が flex アイテムで縮まないため機能しない）
  const tableHeight = pagedShops.length === 0 ? 400 : pagedShops.length >= 15 ? 600 : undefined

  return (
    <Box>
      <Group gap={12} mb={40}>
        <ThemeIcon variant='transparent' color='#4BC1C7'>
          <StorefrontIcon weight='fill' size={36} />
        </ThemeIcon>
        <Title size={24} fw={600} c="#000000CC">店舗管理</Title>
      </Group>

      <Group justify="space-between" mb={16}>
        <Group gap={12}>
          <Button
            variant='filled'
            radius='xl'
            onClick={openCreateShopModal}
            leftSection={UploadLeftSection}
          >
            新規店舗作成
          </Button>

          <FileButton
            onChange={handleUploadCsv}
            resetRef={resetFileRef}
            accept='text/csv'
            disabled={isUploading}
          >
            {(props) =>
              <Button
                {...props}
                variant='filled'
                radius='xl'
                loading={isUploading}
                leftSection={UploadLeftSection}
              >
                CSVから一括追加
              </Button>}
          </FileButton>
        </Group>

        <Group gap={12}>
          <Button
            component='a'
            href={`/sample/shop_sample.csv?d=${Date.now()}`}
            variant='outline'
            radius='xl'
            leftSection={<DownloadSimpleIcon size={16} weight='thin' />}
          >
            CSVテンプレート
          </Button>

          <Button
            variant='outline'
            radius='xl'
            onClick={choiceModal.open}
            leftSection={optionLeftSection}
          >
            選択肢設定
          </Button>

          <Button
            variant='outline'
            radius='xl'
            onClick={sortModal.open}
            leftSection={optionLeftSection}
          >
            都道府県表示順
          </Button>
        </Group>
      </Group>

      {selectedShops.length > 0 && (
        <Alert mt={16} styles={bulkBannerStyles}>
          <Group justify='space-between' wrap='nowrap'>
            <Text>{selectedShops.length}件を選択中</Text>
            <Group gap='xs'>
              <Button
                variant='outline'
                radius='xl'
                size='sm'
                onClick={handleBulkQrDownload}
                loading={qrDownloading}
              >
                QRコードを一括ダウンロード
              </Button>
              <Button
                variant='filled'
                color='invy'
                radius='xl'
                size='sm'
                onClick={handleBulkDelete}
                loading={bulkDeleting}
              >
                一括削除
              </Button>
            </Group>
          </Group>
        </Alert>
      )}
      <Box
        style={{
          border: '1px solid #0000001A',
          borderRadius: '8px',
          overflow: 'hidden',
          flex: 1,
          minWidth: 0,
          width: '100%',
        }}
        mt={16}
      >
        <DataTable
          withColumnBorders
          fetching={isLoading}
          records={pagedShops}
          selectedRecords={selectedShops}
          onSelectedRecordsChange={setSelectedShops}
          idAccessor='id'
          noRecordsText='店舗が登録されていません'
          height={tableHeight}
          styles={{
            root: { border: 'none' },
            header: { backgroundColor: '#F8F9FA' },
          }}
          columns={tableColumns}
        />

        <TablePagination
          page={page}
          onChangePage={setPage}
          perPage={perPage}
          onChangePerPage={(v) => { setPerPage(v); setPage(1) }}
          totalPages={totalPages}
          isEmpty={pagedShops.length === 0}
        />
      </Box>

      <Group justify='end' mt={16}>
        <Button
          component={CSVLink}
          data={csvData}
          headers={csvColumns}
          filename='shops.csv'
          variant='outline'
          radius='xl'
          leftSection={downloadLeftSection}
        >
          登録店舗リストCSV
        </Button>
      </Group>

      <ShopFormModal
        opened={isOpenedShopFormModal}
        onClose={shopFormModal.close}
        client={client}
        shopId={editingShopId}
        onSaved={mutateShops}
      />
      <ShopsOptionModal
        opened={isOpenedChoiceModal}
        onClose={choiceModal.close}
        client={client}
      />
      <PrefectureOrderModal
        opened={isOpenedSortModal}
        onClose={sortModal.close}
        client={client}
      />
      <BaseModal
        opened={isOpenedDeleteModal}
        onClose={deleteModal.close}
        title='店舗の削除'
        width={500}
      >
        <ScrollArea style={{ flex: 1, minHeight: 0 }} bg='#00000008'>
          <Stack gap='sm' p='md'>
            {selectedShop && (
              <>
                <Text>以下の店舗情報を削除します。よろしいですか？</Text>
                <Stack gap={4}>
                  <Text size='sm'>店舗コード: {selectedShop.code}</Text>
                  <Text size='sm'>郵便番号: {selectedShop.zip_code}</Text>
                  <Text size='sm'>都道府県: {prefectureName(selectedShop.prefecture_id)}</Text>
                  <Text size='sm'>住所1: {selectedShop.address1}</Text>
                  <Text size='sm'>住所2: {selectedShop.address2}</Text>
                  <Text size='sm'>住所3: {selectedShop.address3}</Text>
                  <Text size='sm'>店舗名: {selectedShop.name}</Text>
                  <Text size='sm'>店舗名カナ: {selectedShop.kana}</Text>
                  <Text size='sm'>カテゴリー1: {selectedShop.category1}</Text>
                  <Text size='sm'>カテゴリー2: {selectedShop.category2}</Text>
                  <Text size='sm'>カテゴリー3: {selectedShop.category3}</Text>
                </Stack>
                <Text c='red' size='sm'>※一度削除すると元に戻せません。</Text>
                {deleteError && (
                  <Text c='red' size='sm'>{deleteError}</Text>
                )}
              </>
            )}
          </Stack>
        </ScrollArea>

        <Box
          p='md'
          style={{
            borderTop: '1px solid #0000001A',
            backgroundColor: '#fff',
            flexShrink: 0,
          }}
        >
          <Group justify='flex-end' gap={8}>
            <Button
              variant='outline'
              radius='xl'
              color='gray'
              onClick={deleteModal.close}
              disabled={isDeleting}
            >
              キャンセル
            </Button>
            <Button
              variant='filled'
              radius='xl'
              onClick={handleDeleteShop}
              loading={isDeleting}
            >
              削除
            </Button>
          </Group>
        </Box>
      </BaseModal>
    </Box>
  )
}

export default ShopsContainer
